import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill, formatDate, formatNumber } from '@/components/live';
import { usePickers } from '@/hooks/usePickers';
import { optionLabel } from '@/lib/practiceLists';

/**
 * A run at a processing site (owner, 2026-09-27): what came in, what went out, and the recovery
 * between them. The recovery is computed in the view only when both sides share a unit; logs in
 * by the tonne and sawn timber out by the cubic metre have no honest percentage.
 */
interface RunRow {
  id: string; run_code: string | null; site_id: string; site_name: string; site_type: string;
  engagement_id: string | null; party_name: string | null; process_type: string;
  input_description: string | null; input_quantity: number; input_uom: string | null;
  output_description: string | null; output_quantity: number | null; output_uom: string | null; recovery_pct: number | null;
  started_on: string; finished_on: string | null; status: string; remarks: string | null;
}

const PROCESSES = [
  { id: 'sawing', label: 'Sawing' }, { id: 'peeling', label: 'Peeling' }, { id: 'chipping', label: 'Chipping' },
  { id: 'seasoning', label: 'Seasoning' }, { id: 'treatment', label: 'Treatment' }, { id: 'other', label: 'Other' },
];
const STATUSES = [{ id: 'planned', label: 'Planned' }, { id: 'running', label: 'Running' }, { id: 'finished', label: 'Finished' }, { id: 'cancelled', label: 'Cancelled' }];

export default function ProcessRunsScreen() {
  const pick = usePickers(['sites', 'engagements', 'units']);
  return (
    <RegisterScreen<RunRow>
      addLabel="Record a run"
      columns={[
        { key: 'run_code', label: 'Ref', render: (row) => <strong>{row.run_code ?? '--'}</strong> },
        { key: 'site_name', label: 'Site', render: (row) => row.site_name },
        { key: 'process_type', label: 'Process', render: (row) => optionLabel(PROCESSES, row.process_type) },
        { key: 'input_quantity', label: 'In', align: 'right', render: (row) => `${formatNumber(row.input_quantity, 3)} ${row.input_uom ?? ''}` },
        { key: 'output_quantity', label: 'Out', align: 'right', render: (row) => (row.output_quantity != null ? `${formatNumber(row.output_quantity, 3)} ${row.output_uom ?? ''}` : '--') },
        { key: 'recovery_pct', label: 'Recovery', align: 'right', render: (row) => (row.recovery_pct != null ? `${formatNumber(row.recovery_pct, 1)}%` : '--') },
        { key: 'started_on', label: 'Started', render: (row) => formatDate(row.started_on) },
        { key: 'finished_on', label: 'Finished', render: (row) => (row.finished_on ? formatDate(row.finished_on) : '--') },
        { key: 'party_name', label: 'For', render: (row) => row.party_name ?? '--' },
        { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
      ]}
      defaults={{ process_type: 'sawing', status: 'running', started_on: new Date().toISOString().slice(0, 10) }}
      exportName="process-runs"
      fields={[
        { key: 'site_id', label: 'Site', type: 'select', options: pick.processingSites, required: true, hint: 'A sawmill, seasoning unit, treatment plant or processing unit.' },
        { key: 'process_type', label: 'Process', type: 'select', options: PROCESSES, required: true },
        { key: 'engagement_id', label: 'For (engagement)', type: 'select', options: pick.engagements, hint: 'Whose material this is, when the run is done for a party.' },
        { key: 'input_description', label: 'What came in', type: 'text', section: 'Input', placeholder: 'e.g. Teak logs, lot 12' },
        { key: 'input_quantity', label: 'Quantity in', type: 'number', required: true },
        { key: 'input_uom_id', label: 'Unit', type: 'select', options: pick.units, required: true },
        { key: 'output_description', label: 'What came out', type: 'text', section: 'Output', placeholder: 'e.g. Sawn timber 2 in' },
        { key: 'output_quantity', label: 'Quantity out', type: 'number', required: (form) => form.status === 'finished' },
        { key: 'output_uom_id', label: 'Unit', type: 'select', options: pick.units, hint: 'Recovery is shown only when both sides use the same unit.' },
        { key: 'started_on', label: 'Started on', type: 'date', required: true },
        { key: 'finished_on', label: 'Finished on', type: 'date', required: (form) => form.status === 'finished' },
        { key: 'status', label: 'Status', type: 'select', options: STATUSES, required: true },
        { key: 'remarks', label: 'Remarks', type: 'textarea', wide: true },
      ]}
      recordLabel={(row) => `${row.run_code ?? 'run'} at ${row.site_name}`}
      recordNoun="run"
      screenKey="site.register"
      searchColumns={['run_code', 'site_name', 'input_description', 'output_description', 'party_name']}
      stats={[
        { key: 'running', label: 'Running', tone: 'info', match: (row) => row.status === 'running' },
        { key: 'finished', label: 'Finished', tone: 'ok', match: (row) => row.status === 'finished' },
        { key: 'low', label: 'Recovery under 50%', tone: 'warn', match: (row) => row.recovery_pct != null && row.recovery_pct < 50 },
      ]}
      subtitle="What each processing site took in, what it sent out, and the recovery between them."
      table="ops.process_runs"
      title="Process runs"
      validate={(form) => ({
        finished_on: form.finished_on && form.started_on && String(form.finished_on) < String(form.started_on) ? 'Finished before it started.' : undefined,
        input_quantity: form.input_quantity !== '' && Number(form.input_quantity) <= 0 ? 'More than zero.' : undefined,
      })}
      view="process_runs"
    />
  );
}
